<template>
	<q-modal
		v-model="show_preview"
		:content-css="{ maxWidth: getMaxWidth, minWidth: getMinWidth }"
		no-backdrop-dismiss
		no-esc-dismiss
	>
		<q-modal-layout>
			<q-toolbar slot="header">
				<q-btn flat round dense @click="show_preview = false" icon="fas fa-times" />
				<q-toolbar-title>
					{{ $t(game_name) }}
				</q-toolbar-title>
			</q-toolbar>

			<div class="layout-padding">
				<div class="row q-pa-xs justify-center">
					<p class="col-12 text-bold">{{ $t('game_preview') }}</p>
					<div v-if="!game_finished" class="row col-12">
						<p v-if="max_errors !== null && max_errors >= 0" class="col-12 text-center">
							{{ `${$t('errors')}: ${qty_errors} / ${max_errors} ` }}
						</p>
					</div>

					<div>
						<q-carousel
							v-model="slide"
							color="primary"
							:easing="overshoot"
							:autoplay="autoplay"
							class="q-my-sm"
							height="550px"
						>
							<q-carousel-slide
								v-for="(option, index) in options"
								:key="index"
								:class="getCardStyle(index)"
								class="flex flex-center q-mb-lg bg-gray-1 "
							>
								<q-card class="card-style q-py-md" flat>
									<q-card-title class="row q-mx-md">
										<div class="text-overline flex justify-center">
											{{ `${slide + 1}/${number_of_riddles}` }}
											<!-- <q-icon
												v-if="option.correct !== undefined"
												:name="
													option.correct ? 'far fa-check' : 'far fa-times'
												"
												:color="option.correct ? 'positive' : 'negative'"
											/> -->
										</div>
									</q-card-title>
									<q-card-main>
										<div
											v-for="(tip, idx) in option.tips"
											:key="`t${idx}`"
											class="q-body-2 q-my-lg q-mx-md text-weight-regular text-center tip-text"
										>
											{{ tip.label }}
										</div>
									</q-card-main>
									<q-card-actions align="center">
										<q-btn
											v-for="(alt, altIdx) in option.alternatives"
											:key="`alt${altIdx}`"
											flat
											:class="getAnswerStyle(index, altIdx)"
											@click.prevent="selectAnswer(index, alt.value)"
										>
											{{ alt.label }}
										</q-btn>
									</q-card-actions>
								</q-card>
							</q-carousel-slide>

							<q-carousel-control
								v-show="slide > 0"
								slot="control"
								position="top-left"
								class="text-white"
								style="padding: 4px; border-radius: 4px"
							>
								<q-btn
									flat
									color="primary"
									icon="fas fa-arrow-left"
									:disable="slide >= number_of_riddles"
									@click="slide -= 1"
								/>
							</q-carousel-control>

							<q-carousel-control
								v-show="slide < number_of_riddles - 1"
								slot="control"
								position="top-right"
								class="text-white"
								style="padding: 4px; border-radius: 4px"
							>
								<q-btn
									flat
									color="primary"
									icon="fas fa-arrow-right"
									:disable="slide >= number_of_riddles"
									@click="slide += 1"
								/>
							</q-carousel-control>

							<q-carousel-control
								slot="control-progress"
								slot-scope="carousel"
								position="top"
								:offset="[260, 10]"
							>
								<q-progress
									:percentage="carousel.percentage"
									stripe
									color="primary"
									:animate="autoplay"
								/>
							</q-carousel-control>
						</q-carousel>
					</div>

					<div class="col-10 q-mt-lg" v-if="game_finished">
						<p class="text-bold q-mt-sm">{{ $t('result') }}</p>

						<q-slider
							disable
							markers
							snap
							v-model="user_score"
							:color="game_finished ? getSliderColor() : 'blue-6'"
							:min="0"
							:max="number_of_riddles"
						/>

						<p class="q-mt-sm" v-if="max_errors >= 0">
							{{ `${$t('errors')}: ${qty_errors} / ${max_errors}` }}
						</p>

						<p v-if="user_won">{{ $t(success_message) }}</p>
						<p v-else>{{ $t(failure_message) }}</p>
					</div>
				</div>

				<div class="col-xs-12 q-mt-md">
					<div class="row justify-end">
						<q-btn
							color="secondary"
							:label="$t('back')"
							@click="show_preview = false"
						/>
						<q-btn
							v-if="game_finished"
							color="primary"
							class="q-ml-md"
							:label="$t('play_again')"
							@click="resetGame"
						/>
					</div>
				</div>
			</div>
		</q-modal-layout>
	</q-modal>
</template>

<script>
import { easing } from 'quasar'

export default {
	name: 'AdminGameEnigmaPreview',

	props: ['show_game_preview', 'game_name', 'game_options', 'game_max_errors'],

	data() {
		return {
			slide: 0,
			autoplay: false,
			overshoot: easing.overshoot,
			options: [],
			number_of_riddles: 0,
			qty_errors: 0,
			max_errors: null,
			correct_choices: 0,
			game_finished: false,
			user_score: 0,
			user_won: false,
			success_message: `${this.game_name}_success_message`,
			failure_message: `${this.game_name}_failure_message`,
		}
	},

	computed: {
		show_preview: {
			get() {
				return this.show_game_preview
			},
			set() {
				this.$emit('hide_preview', '')
			},
		},

		getMaxWidth() {
			if (this.$q.platform.is.mobile) return '90vw'
			else return '75vw'
		},

		getMinWidth() {
			if (!this.$q.platform.is.mobile) return '50vw'
		},
	},

	methods: {
		getCardStyle(index) {
			return this.slide === index && !this.options[index].selected ? 'card-style' : ''
		},

		getAnswerStyle(optIndex, answerIdx) {
			if (this.game_finished) {
				if (this.options[optIndex].correct) {
					return this.options[optIndex].alternatives[answerIdx].value ===
						this.options[optIndex].selected
						? 'correct'
						: 'btn-not-selected'
				} else {
					return this.options[optIndex].alternatives[answerIdx].value ===
						this.options[optIndex].selected
						? 'incorrect'
						: 'btn-not-selected'
				}
			} else {
				if (!this.options[optIndex].selected) return ''

				return this.options[optIndex].selected ===
					this.options[optIndex].alternatives[answerIdx].value
					? 'btn-selected'
					: 'btn-not-selected'
			}
		},

		getSliderColor() {
			let medium = Math.ceil(this.number_of_riddles / 2)
			if (this.correct_choices - this.qty_errors < medium) return 'red-6'
			else if (this.correct_choices - this.qty_errors == medium) return 'blue-6'
			else return 'green-6'
		},

		selectAnswer(optIdx, answer) {
			this.options[optIdx].selected = answer

			const answered = this.options.filter((opt) => opt.selected)

			setTimeout(() => {
				if (answered.length === this.number_of_riddles) {
					this.checkResults()
				} else {
					if (this.slide + 1 < this.number_of_riddles) this.slide++
					else this.slide = this.options.findIndex((opt) => opt.selected === null)
				}
			}, 1000)
		},

		checkResults() {
			this.options = this.options.map((opt) => ({
				...opt,
				correct: opt.selected === opt.correct_choice,
			}))
			this.correct_choices = this.options.filter((opt) => opt.correct).length
			this.qty_errors = this.options.filter((opt) => opt.correct === false).length
			this.game_finished = true

			setTimeout(() => {
				this.user_score = this.number_of_riddles - this.qty_errors
				this.user_won =
					this.max_errors !== -1
						? this.qty_errors > this.max_errors
							? false
							: true
						: this.correct_choices >= this.number_of_riddles
			}, 750)
		},

		resetGame() {
			this.correct_choices = 0
			this.user_score = 0
			this.slide = 0
			this.user_won = false
			this.game_finished = false
			this.buildGame()
		},

		buildGame() {
			this.options = this.game_options
				.map((riddle) => ({ ...riddle, position: Math.floor(Math.random() * 100) }))
				.sort((a, b) => b.position - a.position)
			this.number_of_riddles = this.options.length
			this.max_errors = this.game_max_errors !== null ? this.game_max_errors : -1
			this.qty_errors = 0
		},
	},

	mounted() {
		this.buildGame()
	},
}
</script>

<style lang="stylus" scoped>
.invisible-border
  border 2px solid transparent

.tip-text
	letter-spacing: 0.05rem

.incorrect
    background rgba(219, 40, 40, 0.5)

.correct
    background rgba(33, 186, 69, 0.5)

.btn-selected
	color blue

.btn-not-selected
	opacity 0.5
	animation not-selected 0.7s

@keyframes not-selected {
	from {
		opacity: 1;
	}
  to {
		opacity: 0.5;
	}
}

.card-style > div
	animation card-anim 1.5s

@keyframes card-anim {
	from {
		background-color: black;
		color: white;
	}
  to {
		background-color: white;
		color: black;
	}
}
</style>
